import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { VendorStatusPill, getDisplayStatus } from '@/components/VendorStatusPill';
import { ComplianceScoreRing } from '@/components/ComplianceScoreRing';
import { getVendor, triggerChase, scoreVendor, generateMagicLink, triggerExtraction, getVendorAllCoverage } from '@/lib/api';
import api from '@/lib/api';
import { useTimezone, formatDateTime, formatDate } from '@/lib/timezone';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft, Mail, Link2, RefreshCw, FileText, Building2,
  Phone, AtSign, User, ShieldCheck, Send, ExternalLink, AlertCircle, CheckCircle2, Plus, Loader2, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export default function VendorDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coverage, setCoverage] = useState(null);
  const [showAssignProperty, setShowAssignProperty] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const tz = useTimezone();
  const { user } = useAuth();

  // Helper to check if document is expired
  const isDocExpired = (doc) => {
    if (!doc.expires_at) return false;
    const now = new Date();
    const expDate = new Date(doc.expires_at);
    return expDate < now;
  };

  // Get proper document status for display
  const getDocStatus = (doc) => {
    // ALWAYS check expiration first
    if (isDocExpired(doc)) return 'expired';
    
    // Check document_state field first
    if (doc.document_state === 'valid') return 'compliant';
    if (doc.document_state === 'expired') return 'expired';
    if (doc.document_state === 'invalid') return 'expired';
    if (doc.document_state === 'pending_review') return 'warning';
    
    // Fallback to extraction_status
    if (doc.extraction_status === 'approved') return 'compliant';
    if (doc.extraction_status === 'completed') return 'warning';
    if (doc.extraction_status === 'failed') return 'expired';
    
    // Fallback to status
    if (doc.status === 'completed') return 'warning';
    if (doc.status === 'failed') return 'expired';
    
    return 'pending';
  };

  const load = async () => {
    try {
      const { data } = await getVendor(id);
      setVendor(data);
      // Load coverage comparison
      try {
        const { data: covData } = await getVendorAllCoverage(id);
        setCoverage(covData);
      } catch { /* coverage may not be available */ }
    } catch { toast.error('Failed to load vendor'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleChase = async () => {
    try {
      await triggerChase(id);
      toast.success('Chase email sent');
      load();
    } catch { toast.error('Failed to send chase'); }
  };

  const handleScore = async () => {
    try {
      const { data } = await scoreVendor(id);
      toast.success(`Score updated: ${data.score}% — ${data.status}`);
      load();
    } catch { toast.error('Failed to score vendor'); }
  };

  const handleMagicLink = async () => {
    try {
      const { data } = await generateMagicLink(id);
      const fullUrl = `${window.location.origin}/portal/${data.token}`;
      
      // Enhanced mobile-friendly clipboard copy
      const copyToClipboard = async (text) => {
        // Method 1: Modern Clipboard API
        if (navigator.clipboard && window.isSecureContext) {
          try {
            await navigator.clipboard.writeText(text);
            return true;
          } catch (e) {
            console.log('Clipboard API failed, trying fallback');
          }
        }
        
        // Method 2: Create temporary input element (works better on mobile)
        const textArea = document.createElement('textarea');
        textArea.value = text;
        
        // Make it invisible but not display:none (needed for iOS)
        textArea.style.position = 'absolute';
        textArea.style.opacity = '0';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        textArea.setAttribute('readonly', '');
        
        document.body.appendChild(textArea);
        
        // For iOS - select the content
        if (navigator.userAgent.match(/ipad|iphone/i)) {
          const range = document.createRange();
          range.selectNodeContents(textArea);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          textArea.setSelectionRange(0, 999999);
        } else {
          textArea.select();
        }
        
        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          return successful;
        } catch (e) {
          document.body.removeChild(textArea);
          return false;
        }
      };
      
      const success = await copyToClipboard(fullUrl);
      if (success) {
        toast.success('Magic link copied! Paste it in a new tab to test.');
      } else {
        // Absolute fallback - show in alert for manual copy
        window.alert(`Copy this link:\n\n${fullUrl}`);
        toast('Link shown - please copy manually', { duration: 3000 });
      }
    } catch { 
      toast.error('Failed to generate link'); 
    }
  };

  const loadProperties = async () => {
    try {
      const { data } = await api.get('/properties');
      setProperties(data.items || []);
    } catch { toast.error('Failed to load properties'); }
  };

  const handleAssignProperty = async () => {
    if (!selectedProperty) {
      toast.error('Please select a property');
      return;
    }
    setAssigning(true);
    try {
      await api.post(`/vendors/${id}/assign-property`, { property_id: selectedProperty });
      toast.success('Vendor assigned to property');
      setShowAssignProperty(false);
      setSelectedProperty('');
      load(); // Reload vendor data
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign property');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignProperty = async (propertyId) => {
    if (!window.confirm('Unassign this vendor from the property?')) return;
    try {
      await api.delete(`/vendors/${id}/unassign-property/${propertyId}`);
      toast.success('Vendor unassigned from property');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unassign');
    }
  };

  const handleDeleteDocument = async (docId, filename) => {
    if (!window.confirm(`Delete "${filename}"? This will permanently remove the document and all extracted data. This action cannot be undone.`)) return;
    
    setDeletingDoc(docId);
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Document deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeletingDoc(null);
    }
  };

  const handleEditVendor = () => {
    setEditForm({
      name: vendor.name,
      email: vendor.email || '',
      phone: vendor.phone || '',
      contact_person: vendor.contact_person || '',
      vendor_type: vendor.vendor_type || 'general',
      address: vendor.address || ''
    });
    setEditing(true);
  };

  const handleSaveVendor = async () => {
    setSaving(true);
    try {
      await api.put(`/vendors/${id}`, editForm);
      toast.success('Vendor updated');
      setEditing(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVendor = async () => {
    if (!window.confirm('Are you sure you want to delete this vendor? This action cannot be undone.')) return;
    try {
      await api.delete(`/vendors/${id}`);
      toast.success('Vendor deleted');
      navigate('/vendors');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  if (loading) {
    return <div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 rounded-xl" /></div>;
  }

  if (!vendor) return <p className="text-muted-foreground">Vendor not found</p>;

  return (
    <div data-testid="vendor-detail-page" className="space-y-6">
      {/* Header */}
      <div data-testid="vendor-detail-header" className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/vendors')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{vendor.name}</h1>
              <VendorStatusPill status={vendor.status} />
            </div>
            <p className="text-sm text-muted-foreground capitalize">{vendor.vendor_type?.replace('_', ' ')}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="vendor-detail-request-docs-button" onClick={handleChase}>
            <Mail className="mr-2 h-4 w-4" /> Request Docs
          </Button>
          <Button variant="outline" data-testid="vendor-detail-send-magic-link-button" onClick={handleMagicLink}>
            <Link2 className="mr-2 h-4 w-4" /> Magic Link
          </Button>
          <Button variant="outline" data-testid="vendor-detail-rescore-button" onClick={handleScore}>
            <RefreshCw className="mr-2 h-4 w-4" /> Re-Score
          </Button>
          <Button variant="destructive" data-testid="vendor-detail-delete-button" onClick={handleDeleteVendor}>
            Delete Vendor
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs data-testid="vendor-detail-tabs" defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="coverage">Coverage</TabsTrigger>
          <TabsTrigger value="documents">Documents ({vendor.documents?.length || 0})</TabsTrigger>
          <TabsTrigger value="properties">Properties ({vendor.properties?.length || 0})</TabsTrigger>
          <TabsTrigger value="communications">Communications</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Card className="lg:col-span-4 rounded-xl">
              <CardContent className="p-6 flex flex-col items-center">
                <ComplianceScoreRing score={vendor.compliance_score} size={160} label="Vendor Score" />
                <div className="mt-3">
                  <VendorStatusPill status={getDisplayStatus(vendor)} />
                </div>
                {vendor.compliance_reasons?.length > 0 && (
                  <div className="mt-4 w-full">
                    <Separator className="mb-3" />
                    <p className="text-xs text-muted-foreground mb-2 font-medium">Compliance Issues:</p>
                    <div className="space-y-2 max-h-48 overflow-auto scrollbar-thin">
                      {vendor.compliance_reasons.map((reason, i) => (
                        <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-muted/50">
                          <AlertCircle className="h-3.5 w-3.5 text-[hsl(36,90%,50%)] shrink-0 mt-0.5" />
                          <span className="text-xs">{typeof reason === 'string' ? reason : reason.reason || JSON.stringify(reason)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Vendor Auto-Approve Toggle */}
                <div className="mt-4 w-full">
                  <Separator className="mb-3" />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium">Auto-Approve</p>
                      <p className="text-xs text-muted-foreground">Auto-approve AI extractions for this vendor</p>
                    </div>
                    <VendorAutoApproveToggle vendorId={id} initialEnabled={vendor.auto_approve_enabled || false} />
                  </div>
                </div>
                {/* Vendor-Paid Toggle */}
                {user?.plan_tier === 'enterprise' && (
                  <div className="mt-3 w-full">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium">Vendor-Paid</p>
                        <p className="text-xs text-muted-foreground">Vendor pays $50/yr to access portal</p>
                      </div>
                      <VendorPaidToggle vendorId={id} initialValue={vendor.vendor_paid || false} />
                    </div>
                  </div>
                )}
                {/* Workers Comp Exemption Toggle */}
                <div className="mt-3 w-full">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium">Workers Comp Exempt</p>
                      <p className="text-xs text-muted-foreground">Pre-check WC exemption for vendor portal</p>
                    </div>
                    <WcExemptToggle vendorId={id} initialValue={vendor.workers_comp_exempt || false} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-8 rounded-xl">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Contact Information</CardTitle>
                <Button variant="outline" size="sm" onClick={handleEditVendor} data-testid="vendor-edit-button">
                  Edit Details
                </Button>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: User, label: 'Contact', value: vendor.contact_person },
                  { icon: AtSign, label: 'Email', value: vendor.email },
                  { icon: Phone, label: 'Phone', value: vendor.phone },
                  { icon: Building2, label: 'Address', value: vendor.address },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-muted"><item.icon className="h-4 w-4 text-muted-foreground" /></div>
                    <div>
                      <p className="text-xs text-muted-foreground">{item.label}</p>
                      <p className="text-sm">{item.value || '-'}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Coverage Comparison (Module 6B) */}
        <TabsContent value="coverage">
          {coverage ? (
            <div className="space-y-4">
              <Card className="rounded-xl">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">Overall Coverage Score</p>
                      <p className="text-3xl font-semibold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {coverage.overall_score}%
                      </p>
                    </div>
                    <Badge variant={coverage.overall_status === 'meets' ? 'default' : coverage.overall_status === 'partial' ? 'secondary' : 'destructive'} className="text-xs capitalize">
                      {coverage.overall_status?.replace('_', ' ')}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{coverage.properties_checked} properties checked</p>
                </CardContent>
              </Card>

              {coverage.property_comparisons?.map((pc, i) => (
                <Card key={i} className="rounded-xl">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        <Building2 className="inline mr-2 h-4 w-4" />
                        {pc.property_name}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{pc.overall_score}%</span>
                        <Badge variant={pc.status === 'meets' ? 'default' : pc.status === 'partial' ? 'secondary' : 'destructive'} className="text-xs capitalize">
                          {pc.status}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {pc.coverage_details?.length > 0 ? (
                      <div className="space-y-2">
                        {pc.coverage_details.map((cd, j) => (
                          <div key={j} className={`flex items-center justify-between p-3 rounded-lg ${cd.meets_requirement ? 'bg-[hsl(142_56%_12%)] border border-[hsl(142_40%_25%)]' : 'bg-[hsl(0_72%_12%)] border border-[hsl(0_60%_25%)]'}`}>
                            <div className="flex items-center gap-2">
                              {cd.meets_requirement ? <CheckCircle2 className="h-4 w-4 text-[hsl(142,56%,50%)]" /> : <AlertCircle className="h-4 w-4 text-[hsl(0,72%,65%)]" />}
                              <span className="text-sm font-medium text-foreground">{cd.label}</span>
                            </div>
                            <div className="flex items-center gap-4 text-sm tabular-nums">
                              <span className="text-muted-foreground">Req: ${Number(cd.required).toLocaleString()}</span>
                              <span className={cd.meets_requirement ? 'text-[hsl(142,56%,55%)] font-medium' : 'text-[hsl(0,72%,65%)] font-medium'}>
                                {cd.actual !== null ? `$${Number(cd.actual).toLocaleString()}` : 'N/A'}
                              </span>
                              {!cd.meets_requirement && cd.gap > 0 && (
                                <Badge variant="destructive" className="text-xs">-{cd.gap_pct}%</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {pc.missing_documents?.length > 0 && (
                      <div className="mt-3 flex gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">Missing:</span>
                        {pc.missing_documents.map((d, k) => <Badge key={k} variant="outline" className="text-xs uppercase">{d}</Badge>)}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">{pc.summary}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="rounded-xl"><CardContent className="py-12 text-center"><Building2 className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" /><p className="text-muted-foreground text-sm">No property assignments found</p></CardContent></Card>
          )}
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents">
          <div className="space-y-4">
            {/* Active Documents */}
            <Card className="rounded-xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Active Documents</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {vendor.documents?.filter(d => !d.archived).length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Document</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Confidence</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vendor.documents.filter(d => !d.archived).map(doc => (
                        <TableRow key={doc.id} className="cursor-pointer hover:bg-muted/50 transition-colors duration-150" onClick={() => navigate(`/documents/${doc.id}`)}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm truncate max-w-[200px]">{doc.filename}</span>
                            </div>
                          </TableCell>
                          <TableCell><Badge variant="secondary" className="text-xs uppercase">{doc.document_type}</Badge></TableCell>
                          <TableCell><VendorStatusPill status={getDocStatus(doc)} /></TableCell>
                          <TableCell className="text-sm tabular-nums">{doc.ai_confidence ? `${(doc.ai_confidence * 100).toFixed(0)}%` : '-'}</TableCell>
                          <TableCell className="text-sm">{doc.expires_at ? formatDate(doc.expires_at, tz) : '-'}</TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <div className="flex gap-1">
                              {doc.status !== 'completed' && (
                                <Button variant="ghost" size="sm" onClick={() => { triggerExtraction(doc.id).then(() => { toast.success('Extraction triggered'); load(); }); }}>
                                  <ShieldCheck className="h-3 w-3 mr-1" /> Extract
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                disabled={deletingDoc === doc.id}
                                onClick={() => handleDeleteDocument(doc.id, doc.filename)}
                                data-testid={`delete-doc-${doc.id}`}
                              >
                                {deletingDoc === doc.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3 text-destructive" />}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="py-12 text-center"><FileText className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" /><p className="text-muted-foreground">No active documents</p></div>
                )}
              </CardContent>
            </Card>

            {/* Archived Documents */}
            {vendor.documents?.filter(d => d.archived).length > 0 && (
              <Card className="rounded-xl border-dashed">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base text-muted-foreground" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        Archive ({vendor.documents.filter(d => d.archived).length})
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">Old document versions - not used for compliance scoring</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Document</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Archived</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vendor.documents.filter(d => d.archived).map(doc => (
                        <TableRow key={doc.id} className="opacity-60 cursor-pointer hover:opacity-80 transition-opacity duration-150" onClick={() => navigate(`/documents/${doc.id}`)}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm truncate max-w-[200px]">{doc.filename}</span>
                            </div>
                          </TableCell>
                          <TableCell><Badge variant="outline" className="text-xs uppercase">{doc.document_type}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{doc.archived_at ? formatDateTime(doc.archived_at, tz) : '-'}</TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              disabled={deletingDoc === doc.id}
                              onClick={() => handleDeleteDocument(doc.id, doc.filename)}
                            >
                              {deletingDoc === doc.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3 text-muted-foreground" />}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Properties */}
        <TabsContent value="properties">
          <Card className="rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Assigned Properties</CardTitle>
              <Button size="sm" onClick={() => { loadProperties(); setShowAssignProperty(true); }}>
                <Plus className="mr-2 h-3 w-3" /> Assign to Property
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {vendor.properties?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Property</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Units</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vendor.properties.map(p => (
                      <TableRow key={p.id} className="hover:bg-muted/50 transition-colors duration-150">
                        <TableCell className="font-medium text-sm cursor-pointer" onClick={() => navigate(`/properties/${p.id}`)}>{p.name}</TableCell>
                        <TableCell className="text-sm cursor-pointer" onClick={() => navigate(`/properties/${p.id}`)}>{p.address}, {p.city}</TableCell>
                        <TableCell className="text-sm capitalize cursor-pointer" onClick={() => navigate(`/properties/${p.id}`)}>{p.property_type}</TableCell>
                        <TableCell className="text-sm cursor-pointer" onClick={() => navigate(`/properties/${p.id}`)}>{p.units}</TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" onClick={() => handleUnassignProperty(p.id)} data-testid={`unassign-property-${p.id}`}>
                            Unassign
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-12 text-center">
                  <Building2 className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
                  <p className="text-muted-foreground mb-4">Not assigned to any properties</p>
                  <Button variant="outline" onClick={() => { loadProperties(); setShowAssignProperty(true); }}>
                    <Plus className="mr-2 h-4 w-4" /> Assign to Property
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Communications */}
        <TabsContent value="communications">
          <Card data-testid="chase-timeline" className="rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Communication History</CardTitle>
              <Button size="sm" onClick={handleChase}><Send className="mr-2 h-3 w-3" /> Send Chase</Button>
            </CardHeader>
            <CardContent>
              {vendor.email_logs?.length > 0 ? (
                <div className="space-y-4">
                  {vendor.email_logs.map((log, i) => (
                    <div key={i} className="flex gap-4 items-start">
                      <div className="flex flex-col items-center">
                        <div className="h-3 w-3 rounded-full bg-primary" />
                        {i < vendor.email_logs.length - 1 && <div className="w-px h-full bg-border mt-1" />}
                      </div>
                      <div className="flex-1 pb-4">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">{log.template_type}</Badge>
                          <Badge variant={log.status === 'opened' ? 'default' : 'outline'} className="text-xs">{log.status}</Badge>
                        </div>
                        <p className="text-sm font-medium mt-1">{log.subject}</p>
                        <p className="text-xs text-muted-foreground mt-1">{log.created_at ? formatDateTime(log.created_at, tz) : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-8 text-center">No communications yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign Property Dialog */}
      <Dialog open={showAssignProperty} onOpenChange={setShowAssignProperty}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Vendor to Property</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Property</Label>
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger data-testid="assign-property-select">
                  <SelectValue placeholder="Choose a property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.filter(p => !vendor.properties?.some(vp => vp.id === p.id)).map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} - {p.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignProperty(false)}>Cancel</Button>
            <Button onClick={handleAssignProperty} disabled={assigning || !selectedProperty} data-testid="assign-property-confirm">
              {assigning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {assigning ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Vendor Dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Vendor Details</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Vendor Name *</Label>
              <Input 
                value={editForm.name || ''} 
                onChange={e => setEditForm(f => ({...f, name: e.target.value}))}
                data-testid="edit-vendor-name"
              />
            </div>
            <div>
              <Label>Vendor Type</Label>
              <Select value={editForm.vendor_type || 'general'} onValueChange={v => setEditForm(f => ({...f, vendor_type: v}))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['general','plumber','electrician','hvac','landscaping','cleaning','roofing','cite','pest_control','pool','security','waste_management','painting'].map(t => (
                    <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contact Person</Label>
              <Input 
                value={editForm.contact_person || ''} 
                onChange={e => setEditForm(f => ({...f, contact_person: e.target.value}))}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                value={editForm.email || ''} 
                onChange={e => setEditForm(f => ({...f, email: e.target.value}))}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input 
                value={editForm.phone || ''} 
                onChange={e => setEditForm(f => ({...f, phone: e.target.value}))}
              />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Input 
                value={editForm.address || ''} 
                onChange={e => setEditForm(f => ({...f, address: e.target.value}))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            <Button onClick={handleSaveVendor} disabled={saving || !editForm.name} data-testid="edit-vendor-save">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function VendorAutoApproveToggle({ vendorId, initialEnabled }) {
  const [enabled, setEnabled] = useState(initialEnabled);
  const handleToggle = async (value) => {
    setEnabled(value);
    try {
      await api.put(`/vendors/${vendorId}/auto-approve`, { auto_approve_enabled: value });
    } catch { setEnabled(!value); }
  };
  return <Switch data-testid="vendor-auto-approve-toggle" checked={enabled} onCheckedChange={handleToggle} />;
}


function VendorPaidToggle({ vendorId, initialValue }) {
  const [value, setValue] = useState(initialValue);
  const handleToggle = async (checked) => {
    setValue(checked);
    try {
      await api.put(`/vendors/${vendorId}/vendor-paid`, { vendor_paid: checked });
      toast.success(checked ? 'Vendor set to Vendor-Paid ($50/yr)' : 'Vendor set to PM-Paid');
    } catch (err) {
      setValue(!checked);
      toast.error(err.response?.data?.detail?.message || 'Failed');
    }
  };
  return <Switch data-testid="vendor-paid-toggle" checked={value} onCheckedChange={handleToggle} />;
}


function WcExemptToggle({ vendorId, initialValue }) {
  const [value, setValue] = useState(initialValue);
  const handleToggle = async (checked) => {
    setValue(checked);
    try {
      await api.put(`/vendors/${vendorId}/wc-exempt`, { workers_comp_exempt: checked });
      toast.success(checked ? 'Workers Comp exemption set - vendor portal toggle pre-checked' : 'Workers Comp exemption removed');
    } catch (err) {
      setValue(!checked);
      toast.error(err.response?.data?.detail || 'Failed to update WC exemption');
    }
  };
  return <Switch data-testid="vendor-wc-exempt-toggle" checked={value} onCheckedChange={handleToggle} />;
}
